import { get } from "../../../logics/src/utils/helpers";
import { getUrl, ROUTES } from "api/urls";
import { logger } from "logging";
import { queryStringFromParams } from "helpers/utils/url";
import { ApiHelper } from "api";
import { contentTypes } from "constants/requestHeaders";
import { createOrderTransformer } from "store/payment/transformers/createOrderTransformer";
import { paytmDetailTransformer } from "../transformers/paytmDetailTransformer";
import { credEligibiltyTransformer } from "../transformers/credEligibiltyTransformer";
import validateBankCardTransformer from "../transformers/validateBankCardTransfomer";
import { PaymentSuccessTransformer } from "../transformers/paymentSuccess";
import { loggingService } from "services/loggingService";
import { verifyMobileEligibilityTransformer } from "../transformers/paymentSuccess/verifyMobileEligibilityTransformer";
import { validateBankOfferTransformer } from "../transformers/validateBankOfferTransformer";
import { ValidateCardBinTransformer } from "../transformers/validateCardBinTransformer";
import {
  CartRefreshInterface,
  CreateOrderInterface,
  FragranceCouponsParams,
  OrdersPaymentResponseInterface,
  PaymentSuccessApiParams,
  SimplEligibilityParams,
  SimplEligibilityStatusParams,
  ValidateBankCardHashType,
  ValidateBankCardType,
  ZestEligibilityParams,
  GetWhatsAppOptInParams,
  SetWhatsAppOptInParams,
  PaymentMethodOffersParams,
  VaultParamsApiParams,
  GiftCardBalanceApiParams,
  PaytmRegisterApiParams,
  PaytmAddMoneyApiParams,
  PaytmValidateOtpParams,
  ValidateBankOfferParams,
  ValidateCardBinParams,
  PaymentInfoApiParams,
  ValidateUPIIDParams,
  GiftCardParams,
  PurchaseGiftCardInterface,
  GiftCardPurchaseResponseInterface,
  LoadWalletInterface,
  CodToPrepaidParams,
  UpdatePaymentInterface,
  CashfreeSessionRequestType,
  CredCheckEligibilityParams,
  PaymentConversionInterface,
  ExpressCheckoutApiParams,
  DeCollectCouponApiParams,
} from "../@types";
import { trackPaymentFailure } from "layouts/Payment/common/dependencies/tracking";
import { checkIsRewardPointsEnabled } from "layouts/Payment/common/dependencies/dependencyhelpers";
import { GetTransFormedWalletPaymentResponse } from "../transformers/wallet/getWalletPaymentResponseTransformer";
import { getStorageData, setStorage } from "helpers/utils/storage";
import {
  giftcardPurchaseResponseTransformer,
  giftCardPurchaseTransformer,
} from "../transformers/giftCardPurchaseTrasnsformer";
import { loadWalletTransformer } from "../transformers/wallet/loadWalletTransformer";
import { getServiceableHeader, isPhoneNumber } from "../../../helpers/index";
import { localStorageService } from "service/storage";
import { checkIsBeautyPlatform } from "helpers/utils/redirectToCheckoutPage";
import { cashFreePaymentTransformer } from "../transformers/cashFreePaymentTransformer";
import { getOrdersPaymentResponseTransformer } from "../transformers/getOrdersPaymentResponseTransformer";
import { getAppVersion } from "helpers/appVersion";
import { INFO_EVENTS } from "logging/newRelicLogger";
import { PAYMENT_STATUS } from "constants/paymentStatus";
import { STORAGE_KEYS } from "constants/index";
import { checkIsGetRefreshFromCacheEnabled } from "helpers/utils/webView";
import { isMobileApp } from "helpers/deviceType";
import transformWidgets from '@nykaa/deals-transformer';
import { offerReversalTransformer } from "../transformers/offerReversalTransformer";


export const defaultParams = {
  source: "react",
  appVersion: getAppVersion(),
  domain: __PLATFORM__,
  catalog_tag_filter: __PLATFORM__,
};

const DEFAULT_ERROR_MESSAGE = "Error in Payment/Service: ";
const DEFAULT_WARN_MESSAGE = "Issue in Payment/Service: ";
// const isDesktop = "desktop" === getActualDeviceType();

export const getInfoForPaymentCreation = async (params: PaymentInfoApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.GET_INFO_PAYMENT_CREATION);
  const queryString = queryStringFromParams(queryParams);
  try {
    if (url && queryString) {
      // const response = paymentInfoCreationQr;
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (!(response?.success)) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "getInfoForPaymentCreation"), { errorResponse: response, queryParams });
      }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getInfoForPaymentCreation`);
    throw error;
  }
};

export const getExpressCheckoutInfo = async (params: ExpressCheckoutApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.EXPRESS_CHCKOUT);

  try {
    if (url) {
      // const response = walletRecommendedJson;
      const { data: response } = await ApiHelper(
        url,
        "post",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (!(response?.success)) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "getExpressCheckoutInfo"), { errorResponse: response, queryParams });
      }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getExpressCheckoutInfo`);
    throw error;
  }
};

export const createOrder = async (
  params: CreateOrderInterface,
  isResponseTransformRequired = true
) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.ORDER_CREATE);

  try {
    if (!checkIsBeautyPlatform()) {
      queryParams.enableNewCart = true;
    }
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      logger.info(INFO_EVENTS.PAYMENT_ORDER_CREATED, {
        orderId: response?.data?.orderNo,
        paymentGateway: response?.data?.paymentGateway,
        paymentMode: response?.data?.paymentMode,
      });
      /**
       * Setting the orderId in localStorage for preventing the multiple
       * order_placed and order_item_placed mixpanel event.
       */
      setStorage({
        key: STORAGE_KEYS.ORDER_ID,
        data: response?.data?.orderNo,
        isLocalStorage: true,
      });

      /**
       * Saves the `nyk_order_id` in localStorage to transition  
       * the user from pre-confirmation to order confirmation  
       * once the payment status is verified as successful  
       * after landing on pre-confirmation.
       * clearing this on order confirmation page
       * and after calling cart refresh if status code is CART NOT FOUND
       */
      setStorage({
        key: STORAGE_KEYS.NYK_ORDER_ID,
        data: response?.data?.orderNo,
        isLocalStorage: true
      });

      return isResponseTransformRequired ? createOrderTransformer(response.data) : response.data;
    }
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: get(response, "message"),
      errorType: "Create order failure response",
    });
    logger.error(new Error(DEFAULT_WARN_MESSAGE + `createOrder ${params?.mode}`), {
      errorResponse: response,
      failurePaymentMethod: params?.mode,
    });
    return response;
  } catch (error) {
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: `${DEFAULT_ERROR_MESSAGE}createOrder`,
      errorType: "Create order API error",
    });
    logger.error(error, { errorMessage:`${DEFAULT_ERROR_MESSAGE}createOrder`, queryParams });
    throw error;
  }
};

export const createOrderCheckoutBFF = async (
  params: CreateOrderInterface,
  isResponseTransformRequired = true
) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.ORDER_CREATE_CHECKOUT_BFF);

  try {
    if (!checkIsBeautyPlatform()) {
      queryParams.enableNewCart = true;
    }
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      logger.info(INFO_EVENTS.PAYMENT_ORDER_CREATED, {
        orderId: response?.data?.orderNo,
        paymentGateway: response?.data?.paymentGateway,
        paymentMode: response?.data?.paymentMode,
      });
      /**
       * Setting the orderId in localStorage for preventing the multiple
       * order_placed and order_item_placed mixpanel event.
       */
      setStorage({
        key: STORAGE_KEYS.ORDER_ID,
        data: response?.data?.orderNo,
        isLocalStorage: true,
      });
      return isResponseTransformRequired ? createOrderTransformer(response.data) : response.data;
    }
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: get(response, "message"),
      errorType: "Create order BFF failure response",
    });
    logger.error(new Error(DEFAULT_WARN_MESSAGE + `createOrder ${params?.mode}`), {
      errorResponse: response,
      failurePaymentMethod: params?.mode,
    });
    return response;
  } catch (error) {
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: `${DEFAULT_ERROR_MESSAGE}createOrderBFF`,
      errorType: "Create order BFF API error",
    });
    logger.error(error, { errorMessage: `${DEFAULT_ERROR_MESSAGE}createOrderBFF`, queryParams });
    throw error;
  }
};

export const purchaseGiftCard = async (params: PurchaseGiftCardInterface) => {
  const giftCardDetails = JSON.parse(getStorageData("giftCard", false))?.formData || {};
  const senderEmailPhone = giftCardDetails?.senderEmail || giftCardDetails?.senderMobile || "";
  const recipientEmailPhone = giftCardDetails?.recipientEmail || "";
  const hasSenderPhoneNumber = isPhoneNumber(senderEmailPhone);
  const hasRecipientPhoneNumber = isPhoneNumber(recipientEmailPhone);

  const giftCardParams = {
    type: 1,
    message: giftCardDetails?.message,
    senderName: giftCardDetails?.senderName,
    cardTypeName: giftCardDetails?.cardTypeName,
    ...(hasSenderPhoneNumber
      ? { senderMobile: senderEmailPhone }
      : {
          senderEmail: senderEmailPhone,
        }),
    ...(giftCardDetails?.isDifferentAmount
      ? { isDifferentAmount: giftCardDetails?.isDifferentAmount }
      : {
          isDifferentAmount: false,
        }),
    ...(giftCardDetails?.sameAmount
      ? { sameAmount: giftCardDetails?.sameAmount }
      : {
          sameAmount: giftCardDetails?.isDifferentAmount ? "" : giftCardDetails?.amount,
        }),
    ...(giftCardDetails?.totalAmount
      ? { totalAmount: giftCardDetails?.totalAmount }
      : {
          totalAmount: giftCardDetails?.amount,
        }),
    ...(giftCardDetails?.recipientDTOList
      ? { recipientDTOList: giftCardDetails?.recipientDTOList }
      : {
          recipientDTOList: [
            {
              amount: giftCardDetails?.amount,
              message: giftCardDetails?.message || "",
              recipientName: giftCardDetails?.recipientName || "",
              ...(hasRecipientPhoneNumber
                ? { recipientMobile: recipientEmailPhone }
                : {
                    recipientEmail: recipientEmailPhone,
                  }),
            },
          ],
        }),
  };
  const queryParams = {
    ...params,
    ...defaultParams,
    ...giftCardParams,
  };
  const url = getUrl(ROUTES.GIFT_CARD_PURCHASE_MULTI);

  try {
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      return giftCardPurchaseTransformer(response.data);
    }
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: get(response, "message"),
      errorType: "Purchase gift card failure response",
    });
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "purchaseGiftCard"), { errorResponse: response });
    return response;
  } catch (error) {
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: `${DEFAULT_ERROR_MESSAGE}purchaseGiftCard`,
      errorType: "Purchase gift card API error",
    });
    logger.error(error, { errorMessage: `${DEFAULT_ERROR_MESSAGE}purchaseGiftCard`, queryParams });
    throw error;
  }
};

export const getOrdersPaymentResponse = async (
  params: OrdersPaymentResponseInterface,
  retryAllowed = false,
  retries = 0
): Promise<any> => {
  const queryParams = {
    ...defaultParams,
    ...params,
  };

  /**
   * query param for tracking purpose
   */
  const url = getUrl(ROUTES.GET_ORDER_PAYMENT_RESPONSE) + `?retries=${retries}`;
  /**
   * if paymentStatus is pending or Failed we silently call payment response api again
   */
  try {
    if (url) {
      // const response = payResponse;
      const { data: response } = await ApiHelper(
        url,
        "post",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (get(response, "success")) {
        const transformedPaymentResponse = getOrdersPaymentResponseTransformer(response.data);
        /**
         * Note: in case of polling retry should not happen for pending case
         */
        if (transformedPaymentResponse.paymentStatus === PAYMENT_STATUS.PENDING && retryAllowed) {
          if (retries < 1) {
            logger.error(new Error("[INFO]: retryInitiated for paymentResponseV2"));
            return getOrdersPaymentResponse(params, retryAllowed, retries + 1);
          }
        }

        return transformedPaymentResponse;
      }

      if (retries < 1) {
        logger.error(new Error("[INFO]: retryInitiated for paymentResponseV2"));
        return getOrdersPaymentResponse(params, retryAllowed, retries + 1);
      }

      logger.error(new Error(DEFAULT_WARN_MESSAGE + "getOrdersPaymentResponse"), {
        errorResponse: response,
      });

      return response;
    }
  } catch (error) {
    if (retries < 1) {
      logger.error(new Error("[INFO]: retryInitiated for paymentResponseV2"));
      return getOrdersPaymentResponse(params, retryAllowed, retries + 1);
    }

    logger.error(error, {
      errorMessage: `${DEFAULT_ERROR_MESSAGE}getOrdersPaymentResponse`,
      queryParams,
    });
    throw error;
  }
};

export const getGiftCardPurchaseResponse = async (params: GiftCardPurchaseResponseInterface) => {
  const queryParams = {
    ...defaultParams,
    ...params,
  };
  const url = getUrl(ROUTES.GET_GIFT_CARD_PAYMENT_RESPONSE_MULTI);

  try {
    if (url) {
      // const response = payResponse;
      const { data: response } = await ApiHelper(
        url,
        "post",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (get(response, "success") && response.data.length > 0) {
        return giftcardPurchaseResponseTransformer(response.data);
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "getGiftCardPurchaseResponse"), { errorResponse: response });
      return response;
    }
  } catch (error) {
    logger.error(error, { errorMessage:`${DEFAULT_ERROR_MESSAGE}getGiftCardPurchaseResponse`, queryParams });
    throw error;
  }
};

export const cartRefresh = async (params: CartRefreshInterface, isConvenienceChargesEnabled: boolean) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.CART_REFRESH);

  try {
    let response = {} as any;
     /**
     * This block is added to handle the webview case, where rather than calling refresh api,
     * refresh data is stored in local storage by apps and then fetched by web.
     * It is done to avoid multiple api calls. To avoid cases issues that can be caused if cart is updated, it is used only once and then deleted from local storage.
     * if data is not present in local storage then call refresh api.
     */
    const isGetRefreshFromCacheEnabled = checkIsGetRefreshFromCacheEnabled();
    if (isGetRefreshFromCacheEnabled && localStorageService.getItem("REFRESH_DATA_WEB_CHECKOUT")) {
      let refreshDataFromApps = JSON.parse(localStorageService.getItem("REFRESH_DATA_WEB_CHECKOUT") || "{}");
      localStorageService.removeItem("REFRESH_DATA_WEB_CHECKOUT");
      /**
       * only data object is passed by the apps
       * since, it is not possible to have success as false and status code as anything other than 200
       * so it is hardcoded
       */
      if (isMobileApp()) {
        response = {
          data: refreshDataFromApps,
          statusCode: 200,
          success: true,
        };
      } else {
        response = refreshDataFromApps;
      }
    } else {
      const { data } = await ApiHelper(
        `${url}`,
        "post",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
          "X-ONEREWARD": checkIsRewardPointsEnabled(),
          "X-APPLY-CART-CHARGES": !!isConvenienceChargesEnabled,
          ...getServiceableHeader(),
        },
        {
          CSRF: true,
        }
      );
      response = data;
    }

    if (!get(response, "success")) {
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "cartRefresh"), {
        errorResponse: response,
        originSource: params?.originSource,
      });
    }
    return response;
  } catch (error) {
    trackPaymentFailure({
      deviceType: params?.deviceType,
      error,
      errorType: "Cart Refresh Failed",
    });

    logger.error(error, `${DEFAULT_ERROR_MESSAGE}cartRefresh`);
    throw error;
  }
};

export const checkoutAggregator = async (params: CartRefreshInterface) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.CHECKOUT_AGGREGATOR);

  try {
    const { data } = await ApiHelper(
      `${url}`,
      "post",
      queryParams,
      {
        "Content-Type": contentTypes.JSON,
        "X-ONEREWARD": checkIsRewardPointsEnabled(),
        ...getServiceableHeader(),
      },
      {
        CSRF: true,
      }
    );
    /**
     * return transformed data for refresh, payment method, vault and getinfo
     */
    const cartRefreshResponse = data.cartReshreshDTO;
      const getInfoForPaymentCreationResponse = data.getInfoForPaymentCreation;
      const paymentMethodResponse = data.paymentMethod;
      const vaultParamsWithUserDetailsResponse = data.vaultParamsWithUserDetailsDTO;
      return {
        cartRefreshResponse,
        getInfoForPaymentCreationResponse,
        paymentMethodResponse,
        vaultParamsWithUserDetailsResponse,
      };
  } catch (error) {
    trackPaymentFailure({
      deviceType: params?.deviceType,
      error,
      errorType: "Checkout Aggregator Failed",
    });

    logger.error(error, `${DEFAULT_ERROR_MESSAGE}cartRefresh`);
    throw error;
  }
};

export const paymentMethodOffers = async (params: PaymentMethodOffersParams, isConvenienceChargesEnabled: boolean) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.OFFERS_PAYMENT_METHOD);
  const queryString = queryStringFromParams(queryParams);

  try {
    const { data: response } = await ApiHelper(`${url}${queryString}`, "get", queryParams, {
      "Content-Type": contentTypes.JSON,
      "X-APPLY-CART-CHARGES": !!isConvenienceChargesEnabled,
    });
    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}paymentMethodOffers`);
    throw error;
  }
};

export const getVaultParamsWithUserDetails = async (params: VaultParamsApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.GET_VAULT_PARAMS_WITH_USER_DETAILS);

  try {
    // const response = vault;
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });

    if (!(get(response, "success"))) {
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "getVaultParamsWithUserDetails"), { errorResponse: response });
    }

    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getVaultParamsWithUserDetails`);
    throw error;
  }
};

export const credCheckEligibility = async (params: CredCheckEligibilityParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.CRED_CHECK_ELIGIBILITY);

  try {
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      return credEligibiltyTransformer(response.data);
    }
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "credCheckEligibility"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}credCheckEligibility`);
    throw error;
  }
};

export const getWalletPaymentResponse = async (params: any) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.WALLET_PAYMWNT_RESPONSE);

  try {
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });

    let paymentStatus = get(response, "data.status");
    if (paymentStatus) {
      const walletPaymentResponse = GetTransFormedWalletPaymentResponse(response.data);
      return { ...walletPaymentResponse, success: response.success };
    }
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "getWalletPaymentResponse"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getWalletPaymentResponse`);
    throw error;
  }
};

export const giftCardBalance = async (params: GiftCardBalanceApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.GIFTCARD_BALANCE);

  try {
    // const response = balance;
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      return response.data;
    }
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "giftCardBalance"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}giftCardBalance`);
    throw error;
  }
};

export const getPaytmDetails = async () => {
  const queryParams = {
    ...defaultParams,
  };

  const url = getUrl(ROUTES.PAYTM_DETAILS);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      // const response = paytmDetail;
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (get(response, "success")) {
        return paytmDetailTransformer(response.data);
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "getPaytmDetails"), { errorResponse: response });
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getPaytmDetails`);
    throw error;
  }
};

export const paytmRegister = async (params: PaytmRegisterApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.PAYTM_REGISTER);

  try {
    // const response = register;
    const { data: response } = await ApiHelper(
      `${url}`,
      "post",
      queryParams,
      {
        "Content-Type": contentTypes.JSON,
      },
      {
        CSRF: true,
      }
    );
    if (get(response, "success")) {
      return response.data;
    }
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "paytmRegister"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}paytmRegister`);
    throw error;
  }
};

export const paytmValidateOtp = async (params: PaytmValidateOtpParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.PAYTM_VALIDATE_OTP);

  try {
    // const response = validatePaytm;
    const { data: response } = await ApiHelper(
      `${url}`,
      "post",
      queryParams,
      {
        "Content-Type": contentTypes.JSON,
      },
      {
        CSRF: true,
      }
    );
    if (get(response, "success")) {
      return paytmDetailTransformer(response.data);
    }
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "paytmValidateOtp"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}paytmValidateOtp`);
    throw error;
  }
};

export const paytmAddMoney = async (params: PaytmAddMoneyApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.PAYTM_ADD_MONEY);

  try {
    // const response = paytmAddMoneyJson;
    const { data: response } = await ApiHelper(
      `${url}`,
      "post",
      queryParams,
      {
        "Content-Type": contentTypes.JSON,
      },
      {
        CSRF: true,
      }
    );
    if (get(response, "success")) {
      return response.data;
    }
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "paytmRegister"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}paytmRegister`);
    throw error;
  }
};

export const validateBankCard = async (params: ValidateBankCardType) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.VALIDATE_BANK_CARD);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (response.data) {
        return validateBankCardTransformer(response.data);
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "validateBankCard"), { errorResponse: response });
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}validateBankCard`);
    throw error;
  }
};

export const validateBankCardHash = async (params: ValidateBankCardHashType) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.VALIDATE_BANK_CARD_HASH);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (response.data) {
        return validateBankCardTransformer(response);
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "validateBankCardHash"), { errorResponse: response });
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}validateBankCardHash`);
    throw error;
  }
};

export const checkZestEligibility = async (params: ZestEligibilityParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.CHECK_ELIGIBILITY_ZEST);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      // const response = checkEligibilityJson;
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (response.data) {
        return response.data;
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "checkEligibilityZest"), { errorResponse: response });
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}checkEligibilityZest`);
    throw error;
  }
};

export const paymentSuccess = async (params: PaymentSuccessApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.PAYMENT_SUCCESS) + `/${params.order_id}`;
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.URL_ENCODED,
          hashValue: params.hashValue,
        },
        {
          CSRF: true,
        }
      );
      if (get(response, "success")) {
        if (!response.data) {
          logger.error(new Error("Response null with success status in paymentSuccess"), { errorResponse: response });
        }
        return PaymentSuccessTransformer(response.data || {});
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "paymentSuccess"), { errorResponse: response });
      return response;
    }
  } catch (error) {
    loggingService.error(error);
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}paymentSuccess`);
    throw error;
  }
};

export const fragranceCoupons = async (params: FragranceCouponsParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.FRAGRANCE_COUPONS);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (get(response, "success")) {
        return response.data;
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "fragranceCoupons"), { errorResponse: response });
      return response;
    }
  } catch (error) {
    loggingService.error(error);
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}fragranceCoupons`);
    throw error;
  }
};

export const verifyMobileEligibility = async () => {
  const queryParams = new FormData();
  queryParams.append("source", defaultParams.source);

  const url = getUrl(ROUTES.VERIFY_MOBILE_ELIGIBILITY);

  /**
   * Added catalog_tag_filter queryParam in url for calling the api on men platform.
   */
  try {
    const { data: response } = await ApiHelper(
      `${url}?catalog_tag_filter=${defaultParams.catalog_tag_filter}`,
      "post",
      queryParams,
      {
        "Content-Type": contentTypes.URL_ENCODED,
      },
      {
        CSRF: true,
      }
    );
    if (!(get(response, "status") === "success")) {
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "verifyMobileEligibility"), { errorResponse: response });
    }
    const transformedResponse = verifyMobileEligibilityTransformer(response);
    return transformedResponse;
  } catch (error) {
    loggingService.error(error);
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}verifyMobileEligibility`);
    throw error;
  }
};

export const getWhatsAppOptInStatus = async (params: GetWhatsAppOptInParams) => {
  // const queryParams = new FormData();
  // queryParams.append('source', defaultParams.source);
  // queryParams.append('phone', params.phone);

  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.GET_WHATSAPP_OPTIN_STATUS);

  const queryString = queryStringFromParams(queryParams);

  try {
    const { data: response } = await ApiHelper(
      `${url}${queryString}`,
      "get",
      queryParams,
      {
        "Content-Type": contentTypes.URL_ENCODED,
      },
      {
        CSRF: true,
      }
    );
    if (!(response?.success)) {
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "getWhatsAppOptInStatus"), { errorResponse: response });
    }

    return response;
  } catch (error) {
    loggingService.error(error);
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getWhatsAppOptInStatus`);
    throw error;
  }
};

export const setWhatsAppOptInStatus = async (params: SetWhatsAppOptInParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url =
    getUrl(ROUTES.SET_WHATSAPP_OPTIN_STATUS) +
    `?orderId=${params.orderId}&emailId=${params.emailId}`;

  try {
    const { data: response } = await ApiHelper(
      `${url}`,
      "PUT",
      queryParams,
      {
        "Content-Type": contentTypes.JSON,
      },
      {
        CSRF: true,
      }
    );
    if (!(response?.success)) {
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "getWhatsAppOptInStatus"), { errorResponse: response });
    }
    return response;
  } catch (error) {
    loggingService.error(error);
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}setWhatsAppOptInStatus`);
    throw error;
  }
};

export const checkSimplEligibility = async (params: SimplEligibilityParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.CHECK_ELIGIBILITY_SIMPL);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      // const response = simplEligibility;
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );

      if (!get(response, "success")) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "checkSimplEligibility"), { errorResponse: response });
      }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}checkSimplEligibility`);
    throw error;
  }
};

export const getSimplEligibilityStatus = async (params: SimplEligibilityStatusParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.CHECK_SIMPL_ELIGIBILITY_STATUS);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      // const response = simplStatus;
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );

      if (!get(response, "success")) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "checkSimplEligibilityStatus"), { errorResponse: response });
      }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}checkSimplEligibilityStatus`);
  }
};

export const getSimplRelinkStatus = async (params: SimplEligibilityStatusParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.SIMPL_RELINK_STATUS);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      // const response = relinkStatus;
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );

      if (!get(response, "success")) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "getSimplRelinkStatus"), { errorResponse: response });
      }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getSimplRelinkStatus`);
  }
};

export const validateBankOffer = async (params: ValidateBankOfferParams) => {
  const queryParams = {
    ...params,
  };

  const url = getUrl(ROUTES.VALIDATE_BANK_OFFER);

  try {
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      return validateBankOfferTransformer(response.data);
    }
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "validateOffer"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, DEFAULT_WARN_MESSAGE + "validateOffer");
  }
};

export const validateCardBin = async (params: ValidateCardBinParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.CARD_VALIDATE);

  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );

      if (get(response, "success")) {
        return ValidateCardBinTransformer(response.data);
      }
      logger.error(new Error(DEFAULT_WARN_MESSAGE + "validateCardBin"), { errorResponse: response });
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}validateCardBin`);
  }
};

/**
 * @description -- hits the API for validating upi id
 * @param customerVpa -- upi id entered by user
 * @returns payerAccountName or error returned by API
 */
export const getValidateUPI = async (params: ValidateUPIIDParams) => {
  const queryParams = {
    ...params,
  };

  const url = getUrl(ROUTES.VALIDATE_VPA);
  const queryString = queryStringFromParams(queryParams);

  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (!get(response, "success")) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "getValidateUPI"), { errorResponse: response });
       }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getValidateUPI`);
    throw error;
  }
};

/**
 * @description - function to call the api for adding Money to
 *  wallet
 *
 */
export const loadWallet = async (params: LoadWalletInterface) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.LOAD_WALLET);

  try {
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      return loadWalletTransformer(response.data);
    }
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: get(response, "message"),
      errorType: "loadWallet API failed",
    });
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "loadWallet"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, { queryParams, errorMessage:`${DEFAULT_ERROR_MESSAGE}loadWallet` });
    throw error;
  }
};

/**
 * @description - function to call the api for purchasing giftcard
 *
 */
export const getGiftCardParams = async (params: GiftCardParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.GET_GIFT_CARD_PARAMS);
  const queryString = queryStringFromParams(queryParams);
  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (!(response?.success)) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "getGiftCardParams"), { errorResponse: response });
      }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getGiftCardParams`);
    throw error;
  }
};

/**
 * @description - function to call the api for Cod to prepaid
 *
 */
export const getCodToPrepaid = async (params: CodToPrepaidParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.GET_COD_TO_PREPAID);
  const queryString = queryStringFromParams(queryParams);
  try {
    if (url && queryString) {
      const { data: response } = await ApiHelper(
        `${url}${queryString}`,
        "get",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      if (!(response?.success)) {
        logger.error(new Error(DEFAULT_WARN_MESSAGE + "validateOrderAndReturnAvailablePaymentMethods"), { errorResponse: response });
      }
      return response;
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE}getCodToPrepaid`);
    throw error;
  }
  // return codToPrepaidRefresh;
};

export const updatePayment = async (params: UpdatePaymentInterface) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.UPDATE_PAYMENT);

  try {
    // const response = create;
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      /** Since we don not get flow from razorpay
       * So, we save orderId and verify this on postPayment for cod to prepaid flow.
       */
      const orderNo = response?.data?.orderNo;
      localStorageService.setItem("COD_TO_PREPAID_ORDER", orderNo);
      return createOrderTransformer(response.data);
    }
    trackPaymentFailure({
      deviceType: params?.deviceType,
      paymentMethod: params?.mode,
      errorMessage: get(response, "message"),
      errorType: "updatePayment API failed",
    });
    logger.error(new Error(DEFAULT_WARN_MESSAGE + "createOrder"), { errorResponse: response });
    return response;
  } catch (error) {
    logger.error(error, { queryParams, errorMessage:`${DEFAULT_ERROR_MESSAGE}createOrder` });
    throw error;
  }
};

export const getCashfreePaymentURL = async (url: string, params: CashfreeSessionRequestType) => {
  try {
    if (url) {
      const { data: response } = await ApiHelper(
        url,
        "post",
        params,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: false,
        }
      );
      return cashFreePaymentTransformer(response);
    }
  } catch (error) {
    logger.error(error, `${DEFAULT_ERROR_MESSAGE} getCashfreePaymentURL`, { params });
    throw error;
  }
};
/**
 * @description - function to hit the /paymentConversion api in paymentFailureFlow
 * @param params - prevOrderId (orderid of previously failed order)
 * @param isResponseTransformRequired - boolean
 * @returns /paymentConversion Api response
 */
export const getPaymentConversionResponse = async (params: PaymentConversionInterface, isResponseTransformRequired = true) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.PAYMENT_CONVERSION);

  try {
    if (checkIsBeautyPlatform()) {
      queryParams.enableNewCart = true;
    }
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      return isResponseTransformRequired ? createOrderTransformer(response.data) : response.data;
    }
    logger.error(new Error(`${DEFAULT_WARN_MESSAGE} getPaymentConversionResponse`), response );
    return response;
  } catch (error) {
    logger.error(error, { errorMessage:`${DEFAULT_ERROR_MESSAGE} getPaymentConversionResponse`, queryParams });
    throw error;
  }
};

export const deCollectCouponApi = async (params: DeCollectCouponApiParams) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.DECOLLECT_COUPONS);

  try {
    if (url) {
      const { data: response } = await ApiHelper(
        url,
        "DELETE",
        queryParams,
        {
          "Content-Type": contentTypes.JSON,
        },
        {
          CSRF: true,
        }
      );
      return response;
    }
  } catch (error) {
    logger.error(error as Error, `${DEFAULT_ERROR_MESSAGE}${url}`);
    return error;
  }
};

export const getDNWidgetsData = async (pageName: string, pageSection: string, platform: string, dnStore: string) => {
  // Generate URL and query string
  const url = getUrl(ROUTES.AD_PLATFORM_API);
  const queryParams = { page_type: pageName, page_section: pageSection, platform, store: dnStore };
  const queryString = queryStringFromParams(queryParams);

  try {
    const { data: response } = await ApiHelper(
      `${url}${queryString}`,
      "get",
      {
        "Content-Type": contentTypes.URL_ENCODED,
      },
      {
        CSRF: true,
      }
    );

    if (response?.errorCode === 0) {
      if (!response.result) {
        logger.error(new Error("Response is null despite success status in DealsPlatform Inventory call"), { errorResponse: response });
        return [];
      }

      // Transform the response data into widgets
      const widgetData = transformWidgets(response.result.slice(0, 1), false);
      return widgetData.widgets || [];
    }

    logger.error(new Error("Non-zero errorCode in API response"), { errorResponse: response });
    return [];
  } catch (error) {
    logger.error(error, "Error while fetching DN widgets data");
    throw error; 
  }
};

export const offerReversal = async (params: any) => {
  const queryParams = {
    ...params,
    ...defaultParams,
  };

  const url = getUrl(ROUTES.OFFER_ROLLBACK);

  try {
    const { data: response } = await ApiHelper(`${url}`, "post", queryParams, {
      "Content-Type": contentTypes.JSON,
    });
    if (get(response, "success")) {
      return offerReversalTransformer(response);
    }

    return response;
  } catch (error) {
    logger.error(error, { errorMessage: `${DEFAULT_ERROR_MESSAGE}offerReversal`, queryParams });
    throw error;
  }
};
